import PropTypes from 'prop-types'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { useEffect, useRef } from 'react'
import { joiResolver } from '@hookform/resolvers/joi'
import { useTranslation } from 'react-i18next'
import joi from 'joi'
import { Box, CircularProgress, Stack, Typography, Divider } from '@mui/material'

import { useFeatureFlag } from '@hapana/shared/web/hooks/useFeatureFlag'

import { SCHEMAS } from '@hapana/shared/utils/validations.util'
import HapanaLogo from '@hapana/shared/web/styles/assets/images/hapana-logo-green-full.png'
import EnvelopeIcon from '@hapana/shared/web/styles/assets/svgs/envelope-small.svg'
import LockAltIcon from '@hapana/shared/web/styles/assets/svgs/lock-alt-small.svg'
import GoogleIcon from '@hapana/shared/web/styles/assets/svgs/google-icon.svg'
import { TextField } from '../../../../components/shared/text-field/text-field.stories'
import { AUTOCOMPLETE_VALUES } from '../../../../components/shared/form/form.constants'
import { TEXT_FIELD_TYPE } from '../../../../components/shared/text-field/text-field.constants'
import Button from '../../button/button.component'

import {
  EMAIL_FIELD_ID,
  EMAIL_FIELD_NAME,
  PASSWORD_FIELD_ID,
  PASSWORD_FIELD_NAME,
} from './login-form.constants'

const joiSchema = joi.object({
  [PASSWORD_FIELD_NAME]: SCHEMAS.password,
  [EMAIL_FIELD_NAME]: SCHEMAS.email,
})
const LoginForm = ({
  onSubmit,
  isLoading,
  defaultValues,
  onForgotPassword,
  onSendLoginLink,
  onEmailChange,
  onGoogleSignIn,
  isGoogleLoading,
  error,
  styles,
  title,
  description,
  googleOnly,
}) => {
  const { t } = useTranslation('common')

  const formMethods = useForm({
    resolver: googleOnly ? undefined : joiResolver(joiSchema),
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const { handleSubmit, control, setValue } = formMethods

  const emailFieldValue = useWatch({ control, name: EMAIL_FIELD_NAME, defaultValue: '' })
  const passwordFieldValue = useWatch({ control, name: PASSWORD_FIELD_NAME, defaultValue: '' })

  // Snapshot values before each submit so we can restore them if the browser
  // clears the fields on a failed auth attempt (common browser security behaviour)
  const lastValuesRef = useRef({ email: '', password: '' })
  useEffect(() => {
    if (emailFieldValue) lastValuesRef.current.email = emailFieldValue
    if (passwordFieldValue) lastValuesRef.current.password = passwordFieldValue
  }, [emailFieldValue, passwordFieldValue])

  // When an error appears, restore fields if the browser cleared them
  useEffect(() => {
    if (!error) return
    const { email, password } = lastValuesRef.current
    if (!emailFieldValue && email) setValue(EMAIL_FIELD_NAME, email)
    if (!passwordFieldValue && password) setValue(PASSWORD_FIELD_NAME, password)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    onEmailChange?.(emailFieldValue || '')
  }, [emailFieldValue, onEmailChange])

  const content = (
    <Box sx={styles.root}>
      <Box sx={styles.flexCenter}>
        <Box component="img" alt={t('login.logoAltText')} src={HapanaLogo} sx={styles.logo} />
      </Box>
      <Box sx={styles.formRoot}>
        <Stack sx={styles.credentialsContainer}>
          <Stack>
            <Typography typography="header1" sx={styles.title}>
              {title}
            </Typography>
            <Typography typography="medium" sx={styles.subtitle}>
              {description}
            </Typography>
          </Stack>
          {error && (
            <Box
              sx={{
                backgroundColor: '#FEE',
                border: '1px solid #FCC',
                borderRadius: 1,
                padding: 1.5,
                color: '#D8000C',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          {!googleOnly && (
            <>
              <TextField
                name={EMAIL_FIELD_NAME}
                label={t('admin:login.email')}
                startAdornment={EnvelopeIcon}
                id={EMAIL_FIELD_ID}
                autoComplete={AUTOCOMPLETE_VALUES.email}
                disabled={isLoading}
                inputProps={{
                  autoComplete: AUTOCOMPLETE_VALUES.email,
                  name: EMAIL_FIELD_NAME,
                  autoFocus: true,
                  onKeyDown: (e) => {
                    if (e.key === 'Enter') handleSubmit(onSubmit)()
                  },
                }}
                InputLabelProps={{ shrink: true }}
                placeholder="Email"
              />
              <TextField
                name={PASSWORD_FIELD_NAME}
                label={t('login.password')}
                startAdornment={LockAltIcon}
                textFieldType={TEXT_FIELD_TYPE.PASSWORD}
                id={PASSWORD_FIELD_ID}
                autoComplete={AUTOCOMPLETE_VALUES.currentPassword}
                disabled={isLoading}
                inputProps={{
                  autoComplete: AUTOCOMPLETE_VALUES.currentPassword,
                  name: PASSWORD_FIELD_NAME,
                  onKeyDown: (e) => {
                    if (e.key === 'Enter') handleSubmit(onSubmit)()
                  },
                }}
                InputLabelProps={{ shrink: true }}
                placeholder="Password"
              />
              <Button
                label={isLoading ? <CircularProgress size={24} /> : t('login.buttonLabel')}
                sx={styles.button}
                disabled={!emailFieldValue || !passwordFieldValue || isLoading || isGoogleLoading}
                size="large"
              />
              {onGoogleSignIn && (
                <Stack spacing={2} sx={{ width: '100%' }}>
                  <Divider sx={{ color: '#6F7074', fontSize: '0.875rem' }}>OR</Divider>
                  <Button
                    label={isGoogleLoading ? <CircularProgress size={24} /> : 'Sign in with Google'}
                    sx={{
                      ...styles.button,
                      backgroundColor: 'white',
                      color: '#3C4043',
                      border: '1px solid #E4E4E5',
                      '&:hover': {
                        backgroundColor: '#F8F9FA',
                        border: '1px solid #DBDBDC',
                      },
                    }}
                    disabled={isLoading || isGoogleLoading}
                    size="large"
                    onClick={onGoogleSignIn}
                    startIcon={GoogleIcon}
                    variant="outlined"
                  />
                </Stack>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  typography="header4"
                  sx={styles.getHelpSigningLabel}
                  onClick={onForgotPassword}
                >
                  Reset password
                </Typography>
                {onSendLoginLink && (
                  <Typography
                    typography="header4"
                    sx={styles.getHelpSigningLabel}
                    onClick={onSendLoginLink}
                  >
                    Send a login link
                  </Typography>
                )}
              </Box>
            </>
          )}
          {googleOnly && onGoogleSignIn && (
            <Button
              label={isGoogleLoading ? <CircularProgress size={24} /> : 'Sign in with Google'}
              sx={{
                ...styles.button,
                backgroundColor: 'white',
                color: '#3C4043',
                border: '1px solid #E4E4E5',
                '&:hover': {
                  backgroundColor: '#F8F9FA',
                  border: '1px solid #DBDBDC',
                },
              }}
              disabled={isGoogleLoading}
              size="large"
              onClick={onGoogleSignIn}
              startIcon={GoogleIcon}
              variant="outlined"
            />
          )}
        </Stack>
      </Box>
      <Box sx={styles.backgroundPatternImage}></Box>
    </Box>
  )

  if (googleOnly) {
    return <Box sx={styles.form}>{content}</Box>
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} style={styles.form} autoComplete="on">
        {content}
      </form>
    </FormProvider>
  )
}

LoginForm.propTypes = {
  onSubmit: PropTypes.func,
  onForgotPassword: PropTypes.func,
  onSendLoginLink: PropTypes.func,
  onEmailChange: PropTypes.func,
  onGoogleSignIn: PropTypes.func,
  isLoading: PropTypes.bool,
  isGoogleLoading: PropTypes.bool,
  error: PropTypes.string,
  defaultValues: PropTypes.object,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  styles: PropTypes.object,
  googleOnly: PropTypes.bool,
}

LoginForm.defaultProps = {
  isLoading: false,
  isGoogleLoading: false,
  error: null,
  defaultValues: {},
  onGoogleSignIn: null,
  onSendLoginLink: null,
  onEmailChange: null,
  googleOnly: false,
}

export default LoginForm
